import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Card,
  CardContent,
  Tooltip,
  useTheme,
  Button,
} from "@mui/material";

import stylesProjectPage from '../../styles/projectPage/index.module.scss';
import styles from '../../styles/component.module.scss';
import { PlusIcon, TrashIcon, PencilIcon, FolderIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

//utility
import { useNavigate } from "react-router-dom";
import { selectProject } from "../../features/projects/ProjectSlice";
import { setActivePage } from "../../features/navigation/navigationSlice";
import { clearGeneratedTestCase } from "../../features/testCases/testCasesSlice";
import DEFAULT_ROUTES from "../../constants/routes";
import { useSnackbar } from "../../utils/useSnackbar";
//components
import CustomButton from "../Button/customButton/CustomButton";
import CreateProjectModal from "../Modal/CreateProjectModal";
import EditProjectModal from "../Modal/EditProjectModal";
import ConfirmationModal from "../Modal/ConfirmationModal";
// import TextSkeletonContainer from "../Skeleton/TextSkeletonContainer";
// import GridLoader from "../Loaders/gridloader";
import ProjectGridSkeleton from "../Loaders/ProjectGridSkeleton";
import { getProjectsList, deleteProject } from "../../utils/api/project_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import ProjectCard from "./projectCard";
import ProjectEmptyScreen from "./ProjectEmptyScreen";
import ButtonComponent from "../customButton";
import QuickSearchInput from "../Query/QuickSearchInput";
import quickSearchBarStyles from '../../styles/quickSearchBar.module.scss';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';

const EMPTY_STATE_TEXT = {
  noSearchResults: "No projects found",
};

const ProjectGridTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [quickSearchValue, setQuickSearchValue] = useState("");
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editProject, setEditProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [newlyCreatedProjects, setNewlyCreatedProjects] = useState(new Set());
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const activePage = useSelector((state) => state.navigation.activePage);

  // Add CSS animation for new projects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromTop {
        0% {
          opacity: 0;
          transform: translateY(-15px) scale(0.98);
        }
        100% {
          opacity: 1;
          transform: translateY(0px) scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Pagination state for grid view - use large pageSize to show all projects
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 1000  // Large page size to show all projects in grid view
  });

  // Fetch projects from the backend when the component mounts or pagination changes
  useEffect(() => {
    (async()=>{
      setLoading(true);
      // Convert 0-based page index to 1-based for API
      const result = await getProjectsList({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize
      });
      if(result.status === STATUS.SUCCESS){
        const data = result.data;
        const rows = data?.rows || [];
        setProjects(rows);
        setFilteredProjects(rows);
      }
      setLoading(false);
    })();
  }, [paginationModel.page, paginationModel.pageSize]);

  // Client-side quick search over name / prefix / description
  useEffect(() => {
    const raw = typeof quickSearchValue === "string" ? quickSearchValue : "";
    const term = raw.trim().toLowerCase();

    if (!term) {
      setFilteredProjects(projects);
      return;
    }

    const handle = setTimeout(() => {
      const next = projects.filter((project) => {
        const name = (project.name || "").toLowerCase();
        const prefix = (project.prefix || "").toLowerCase();
        const description = (project.description || "").toLowerCase();
        return (
          name.includes(term) ||
          prefix.includes(term) ||
          description.includes(term)
        );
      });
      setFilteredProjects(next);
    }, 1000);

    return () => {
      clearTimeout(handle);
    };
  }, [projects, quickSearchValue]);

  const openDeleteModal = (event, projectId) => {
    if(event) event.stopPropagation();
    setDeleteProjectId(projectId);
    setDeleteModalOpen(true);
  };

  const openEditModal = (event, project) => {
    if(event) event.stopPropagation();
    setIsEditing(true);
    setEditProject(project);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteProjectId(null);
  };

  const confirmDeleteProject = async () => {
    try {
      const projectToDelete = [deleteProjectId];
      
      const result = await deleteProject({ ids: projectToDelete });
      
      if (result.status === STATUS.SUCCESS) {
        setProjects((prevProjects) =>
          prevProjects.filter((proj) => proj.id !== deleteProjectId)
        );
        if (selectedProject?.id === deleteProjectId) {
          dispatch(selectProject(null)); // Clear selected project if it's the one being deleted
        }
        closeDeleteModal();
        handleSnackbar("delete");
      } else {
        handleSnackbar("error delete", result.error);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      handleSnackbar("error delete", error?.message);
    }
  };

  const handleOnProjectClick = (project) => {
    // dispatch(selectProject(project));
    // dispatch(clearGeneratedTestCase());
    // dispatch(setActivePage("Test Cases"));
    navigate(
      `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${project.id}/test-cases`
    );
  };

  const handleCreateProject = (newProject) => {
    setProjects([...projects, newProject]);
    // Mark this project as newly created
    setNewlyCreatedProjects(prev => new Set([...prev, newProject.id]));
    
    // Clear the "new" flag after animation completes
    setTimeout(() => {
      setNewlyCreatedProjects(prev => {
        const updated = new Set(prev);
        updated.delete(newProject.id);
        return updated;
      });
    }, 1000); // Clear after 1 second
    
    handleSnackbar("add");
  };

  const handleEditProject = (updatedProject) => {
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === updatedProject.id ? updatedProject : project
      )
    );
    handleSnackbar("update");
  };
  // Centralized function to handle Snackbar logic
  const handleSnackbar = (actionType, errorMessage) => {
    let message = "";
    let severity = "success";

    switch (actionType) {
      case "add":
        message = "Project added successfully!";
        severity = "success";
        break;
      case "update":
        message = "Project updated successfully!";
        severity = "success";
        break;
      case "delete":
        message = "Project deleted successfully!";
        severity = "success";
        break;
      case "error update":
        message = "";
        severity = "error";
        break;
      case "error save":
        message = "Error in Saving Project!";
        severity = "error";
        break;
      case "error delete":
        message = "Error in Deleting Project!";
        severity = "error";
        break;
      case "error create":
        message = `Error creating Project`;
        severity = "error";
        break;
      case "error duplicate":
        message = `Project with this name already exists!`;
        severity = "error";
        break;
      default:
        message = "Action completed successfully!";
        severity = "info";
    }

    if(actionType === "error create" || actionType === "error update" || actionType === "error duplicate"){
      showSnackbar(errorMessage, severity);
    }else{
      showSnackbar(`${message}${errorMessage ? `: ${errorMessage}` : ""}`, severity);
    }
  };

  const isSearchActive = quickSearchValue.trim().length > 0;
  const hasNoProjects = projects.length === 0;
  const hasNoSearchResults = isSearchActive && filteredProjects.length === 0;
  const projectsToDisplay = isSearchActive ? filteredProjects : projects;

  return (
    <>
      <>
        <Box className={stylesProjectPage['grid-tab-header']}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2, minWidth: 0 }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }} />
            <Box className={quickSearchBarStyles.quickSearchBarWrapFixed}>
              <QuickSearchInput
                entityType="project"
                value={quickSearchValue}
                onChange={setQuickSearchValue}
                placeholder="Search projects..."
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
            <Tooltip title="Add Project">
              <ButtonComponent
                variant="primary"
                className="white bg-blue600"
                startIcon={<PlusIcon className="icon" />}
                onClick={() => setIsCreating(true)}
                ctaText="Add"
                disabled={loading}
              />
            </Tooltip>
            </Box>
          </Box>
        </Box>
        <Box className={`${styles['cover-container-col-auto']} ${stylesProjectPage['grid-container']}`}>
          {
            loading ? <ProjectGridSkeleton /> : hasNoSearchResults ? (
              <div className={stylesProjectPage['search-empty-state']}>
                <p className={stylesProjectPage['search-empty-text']}>
                  {EMPTY_STATE_TEXT.noSearchResults}
                </p>
              </div>
            ) : hasNoProjects ? (
              <ProjectEmptyScreen onCreate={() => setIsCreating(true)} />
            ) : (
              <Box className={stylesProjectPage['card-container']}>
                {projectsToDisplay.map((project, index) => {
                  const isNewProject = newlyCreatedProjects.has(project.id);

                  return (
                    <ProjectCard 
                      isNew={isNewProject}
                      index={index}
                      onEdit={openEditModal}
                      onDelete={openDeleteModal}
                      project={project}
                      key={project.id}
                      selectedProject={selectedProject}
                    />
                  );
                })}
              </Box>
            )
          }
        </Box>
      </>
      {/* Popup Modals */}
      <>
        {/* Create Project Modal */}
        <CreateProjectModal
          open={isCreating}
          onClose={() => setIsCreating(false)}
          onCreate={handleCreateProject}
          onError={(errorType, errorMessage) =>
            handleSnackbar(errorType, errorMessage)
          }
          projects={projects}
        />

        {/* Edit Project Modal */}
        <EditProjectModal
          open={isEditing}
          onClose={() => setIsEditing(false)}
          onEdit={handleEditProject}
          onError={(errorType, errorMessage) =>
            handleSnackbar(errorType, errorMessage)
          }
          projectToUpdate={editProject}
          projects={projects}
        />

        {/* Delete Project Modal */}
        <ConfirmationModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDeleteProject}
          title={getDeleteModalTitle()}
          message={getDeleteConfirmMessage('project', 1, false)}
        />
      </>
    </>
  );
};

export default ProjectGridTab;
